import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ordersAPI } from '../../services/api';
import { Package, Clock, ChevronDown, ChevronUp } from 'lucide-react';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  shipped: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    ordersAPI.getMyOrders()
      .then((res) => setOrders(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 flex items-center gap-3">
        <Package className="w-8 h-8" /> Mis Órdenes
      </h1>

      {orders.length === 0 ? (
        <div className="text-center py-20">
          <Package className="w-20 h-20 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-xl text-gray-500 dark:text-gray-400 mb-4">No tienes órdenes aún</p>
          <Link to="/store" className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700">
            Ir a la Tienda
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750"
                onClick={() => setExpanded(expanded === order.id ? null : order.id)}
              >
                <div className="flex items-center gap-4">
                  <Package className="w-5 h-5 text-orange-600" />
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{order.order_number}</p>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[order.status] || ''}`}>
                    {order.status}
                  </span>
                  <span className="font-bold text-gray-900 dark:text-white">${order.total_amount.toFixed(2)}</span>
                  {expanded === order.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>
              {expanded === order.id && (
                <div className="border-t dark:border-gray-700 p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Método de Pago</p>
                      <p className="font-medium capitalize">{order.payment_method}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Pago</p>
                      <p className="font-medium capitalize">{order.payment_status}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Subtotal</p>
                      <p className="font-medium">${order.subtotal?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">VU Total</p>
                      <p className="font-medium">${order.total_vu?.toFixed(2)}</p>
                    </div>
                  </div>
                  {order.items?.length > 0 && (
                    <div className="border-t dark:border-gray-700 pt-3">
                      <p className="text-sm font-semibold mb-2">Productos</p>
                      {order.items.map((item) => (
                        <div key={item.id} className="flex justify-between text-sm py-1">
                          <span className="text-gray-600 dark:text-gray-300">
                            {item.product_name} x{item.quantity}
                          </span>
                          <span>${(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {order.tracking_number && (
                    <div className="border-t dark:border-gray-700 pt-3">
                      <p className="text-sm text-gray-500">Número de Seguimiento:</p>
                      <p className="font-medium">{order.tracking_number}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderHistory;
